(function () {
  if (customElements.get('ce-product-media') !== undefined) {
    return;
  }

  const LOAD_STATE = {
    NOT_LOADED: '0',
    LOADED: '2',
  };

  class ProductMedia extends HTMLParsedElement {
    constructor() {
      super();

      this.queuedAttributeChangedCallbackParams = [];

      this.options = {
        selectedVariantMediaOnly: this.hasAttribute('selected-variant-media-only') || false,
        enableVariantSelectionWithThumbnails: this.hasAttribute('enable-variant-selection-with-thumbnails') || false,
      };

      this.selectors = {
        product: 'ce-product',
        slideshow: 'ce-slideshow',
        content: '[data-product-media-content]',
        list: '[data-product-media-list]',
        variantImage: '[data-variant-image]',
        productMediaVideo: 'ce-product-media-video',
        productMediaModel: 'ce-product-media-model',
        modelViewer: 'model-viewer',
        buttonXR: '[data-shopify-xr]',
        thumbnailList: '[data-product-media-thumbnail-list]',
        thumbnailListItem: '[data-product-media-thumbnail-list-item]',
        thumbnailButton: '[data-product-media-thumbnail-button]',
        thumbnailButtonWithFocus: '[data-product-media-thumbnail-button]:focus',
        loader: '[data-product-media-loader]',
        loaderTarget: '[data-product-media-loader-target]',
        json: '[data-product-media-json]',
      };

      this.elements = null;

      this.defaultModelId = this.getAttribute('default-model-id');

      this.timeoutDebounceScrollThumbnailList = null;
      this.mapVariantMediaIds = null;

      this.handleProductVariantId = this.handleProductVariantId.bind(this);
      this.handleSlideshowVisibleSlides = this.handleSlideshowVisibleSlides.bind(this);
      this.handleClickThumbnailButton = this.handleClickThumbnailButton.bind(this);
    }

    get currentSlide() {
      return parseInt(this.getAttribute('current-slide'));
    }

    set currentSlide(value) {
      this.setAttribute('current-slide', value);
    }

    get currentThumbnail() {
      return parseInt(this.getAttribute('current-thumbnail'));
    }

    set currentThumbnail(value) {
      this.setAttribute('current-thumbnail', value);
    }

    get loadState() {
      return this.getAttribute('load-state');
    }

    set loadState(value) {
      this.setAttribute('load-state', value);
    }

    get variantId() {
      return this.getAttribute('variant-id');
    }

    set variantId(value) {
      this.setAttribute('variant-id', value);
    }

    static get observedAttributes() {
      return ['current-slide', 'current-thumbnail', 'load-state', 'variant-id'];
    }

    attributeChangedCallback(...params) {
      if (!this.parsed) {
        this.queuedAttributeChangedCallbackParams.push(params);
        return;
      }

      const [name, oldValue, newValue] = params;

      if (oldValue === newValue) {
        return;
      }

      switch (name) {
        case 'current-slide':
          if (oldValue !== null) {
            const currentListItem = this.elements.list.children[oldValue];
            const video = currentListItem.querySelector(this.selectors.productMediaVideo);
            const model = currentListItem.querySelector(this.selectors.productMediaModel);

            if (video !== null) {
              video.currentState = 'inactive';
            }

            if (model !== null) {
              model.currentState = 'inactive';
            }
          }

          if (newValue !== null) {
            const currentListItem = this.elements.list.children[newValue];
            const model = currentListItem.querySelector(this.selectors.productMediaModel);

            if (model !== null) {
              this.elements.buttonXR.setAttribute('data-shopify-model3d-id', model.id);
            } else {
              this.elements.buttonXR.setAttribute('data-shopify-model3d-id', this.defaultModelId);
            }

            if (!this.options.selectedVariantMediaOnly) {
              this.currentThumbnail = newValue;
            }
          }

          break;
        case 'current-thumbnail':
          if (oldValue !== null) {
            if (oldValue !== '-1') {
              const oldThumbnail = this.elements.thumbnailButtons[oldValue] || this.elements.thumbnailListItems[oldValue];
              oldThumbnail.removeAttribute('aria-current');
            }
          }

          if (newValue !== null) {
            if (newValue !== '-1') {
              const newThumbnail = this.elements.thumbnailButtons[newValue] || this.elements.thumbnailListItems[newValue];
              newThumbnail.setAttribute('aria-current', 'true');

              clearTimeout(this.timeoutDebounceScrollThumbnailList);
              this.timeoutDebounceScrollThumbnailList = setTimeout(() => {
                this.scrollThumbnailList(newValue);

                if (this.loadState !== LOAD_STATE.LOADED) {
                  setTimeout(() => {
                    this.loadState = LOAD_STATE.LOADED;
                  }, 1000);
                }
              }, 100);
            } else {
              if (this.loadState !== LOAD_STATE.LOADED) {
                this.loadState = LOAD_STATE.LOADED;
              }
            }
          }

          break;
        case 'load-state':
          if (newValue !== null) {
            if (newValue === LOAD_STATE.LOADED) {
              requestAnimationFrame(() => {
                for (let element of this.elements.loaderTargets) {
                  element.classList.add('hidden');
                }
              });

              const keyframes = [{ opacity: 0 }, { opacity: 1 }];

              const options = {
                duration: Themu.prefersReducedMotion ? 0 : 500,
                easing: Themu.animation.easeIn,
                fill: 'both',
              };

              const animation = this.elements.content.animate(keyframes, options);
              animation.addEventListener('finish', () => {
                animation.commitStyles();
                animation.cancel();

                this.elements.loader.classList.add('hidden');
              });
            }
          }

          break;
        case 'variant-id':
          if (newValue !== null) {
            const mediaId = this.mapVariantMediaIds[newValue];

            if (this.options.selectedVariantMediaOnly) {
              this._toggleSlides(mediaId);
            }

            let hasVariantImage = false;
            for (let slide of this.elements.list.children) {
              if (slide.dataset.mediaId === mediaId) {
                hasVariantImage = true;
                this.elements.slideshow.scrollToSlide(slide);
                break;
              }
            }

            if (!hasVariantImage) {
              const slide = this.elements.list.querySelector('[data-position="0"]');
              this.elements.slideshow.scrollToSlide(slide);
            }

            if (this.options.selectedVariantMediaOnly) {
              let isVariantThumbnail = false;
              for (let i = 0; i < this.elements.thumbnailListItems.length; i++) {
                const thumbnail = this.elements.thumbnailListItems[i];

                if (thumbnail.dataset.variantId !== newValue) {
                  continue;
                }

                this.currentThumbnail = i;
                isVariantThumbnail = true;
                break;
              }

              if (!isVariantThumbnail) {
                this.currentThumbnail = -1;
              }
            }
          }

          break;
      }
    }

    parsedCallback() {
      this.elements = {
        product: this.closest(this.selectors.product),
        slideshow: this.closest(this.selectors.slideshow),
        content: this.querySelector(this.selectors.content),
        list: this.querySelector(this.selectors.list),
        buttonXR: this.querySelector(this.selectors.buttonXR),
        thumbnailList: this.querySelector(this.selectors.thumbnailList),
        thumbnailListItems: Array.from(this.querySelectorAll(this.selectors.thumbnailListItem)),
        thumbnailButtons: Array.from(this.querySelectorAll(this.selectors.thumbnailButton)),
        loader: this.querySelector(this.selectors.loader),
        loaderTargets: this.querySelectorAll(this.selectors.loaderTarget),
        json: this.querySelector(this.selectors.json),
      };

      this.createMapVariantMediaIds();

      this.elements.product.addEventListener('ce-product:variant-id', this.handleProductVariantId);
      this.elements.slideshow.addEventListener('ce-slideshow:visible-slides', this.handleSlideshowVisibleSlides);

      for (let thumbnailButton of this.elements.thumbnailButtons) {
        thumbnailButton.addEventListener('click', this.handleClickThumbnailButton);
      }

      for (let params of this.queuedAttributeChangedCallbackParams) {
        this.attributeChangedCallback(...params);
      }
    }

    disconnectedCallback() {
      this.elements.product.removeEventListener('ce-product:variant-id', this.handleProductVariantId);
      this.elements.slideshow.removeEventListener('ce-slideshow:visible-slides', this.handleSlideshowVisibleSlides);

      for (let thumbnailButton of this.elements.thumbnailButtons) {
        thumbnailButton.removeEventListener('click', this.handleClickThumbnailButton);
      }
    }

    _toggleSlides(mediaId) {
      let position = 0;

      for (let slide of this.elements.list.children) {
        if ('variantImage' in slide.dataset) {
          if (slide.dataset.mediaId === mediaId) {
            slide.classList.replace('hidden', 'block');
            slide.dataset.position = position;
            position = position + 1;
          } else {
            slide.classList.replace('block', 'hidden');
            this.elements.slideshow.removeSlide(slide.dataset.position);
            slide.dataset.position = -1;
          }
        } else {
          slide.classList.replace('hidden', 'block');
          slide.dataset.position = position;
          position = position + 1;
        }
      }

      this.elements.slideshow.totalSlides = position;
    }

    handleProductVariantId(event) {
      this.variantId = event.detail;
    }

    handleClickThumbnailButton(event) {
      const target = event.target.closest(this.selectors.thumbnailButton);

      if (this.options.enableVariantSelectionWithThumbnails && 'variantId' in target.dataset) {
        this.elements.product.setVariantWithId(target.dataset.variantId);
        return;
      }

      const position = target.dataset.position;
      const slide = this.elements.list.children[position];
      this.elements.slideshow.scrollToSlide(slide);
    }

    handleSlideshowVisibleSlides(event) {
      const visibleSlides = event.detail;

      if (visibleSlides.length === 0) {
        return;
      }

      this.currentSlide = Math.min(...visibleSlides);
    }

    createMapVariantMediaIds() {
      const obj = JSON.parse(this.elements.json.textContent);
      this.mapVariantMediaIds = obj;

      this.elements.json.remove();
    }

    scrollThumbnailList(position) {
      if (window.innerWidth < 1280) {
        const { left: listLeft, right: listRight, width: listWidth } = this.elements.thumbnailList.getBoundingClientRect();

        const currentThumbnailListItem = this.elements.thumbnailListItems[position];
        const { left: thumbnailLeft, right: thumbnailRight, width: thumbnailWidth } = currentThumbnailListItem.getBoundingClientRect();

        if (thumbnailLeft < listLeft) {
          const left = currentThumbnailListItem.offsetLeft - (window.innerWidth < 768 ? 16 : 5);

          this.elements.thumbnailList.scrollTo({
            left: left,
            top: 0,
            behavior: Themu.prefersReducedMotion ? 'instant' : 'smooth',
          });
        }

        if (thumbnailRight > listRight) {
          const left = currentThumbnailListItem.offsetLeft - listWidth + thumbnailWidth + (window.innerWidth < 768 ? 16 : 5);

          this.elements.thumbnailList.scrollTo({
            left: left,
            top: 0,
            behavior: Themu.prefersReducedMotion ? 'instant' : 'smooth',
          });
        }
      } else {
        const { top: listTop, bottom: listBottom, height: listHeight } = this.elements.thumbnailList.getBoundingClientRect();

        const currentThumbnailListItem = this.elements.thumbnailListItems[position];
        const { top: thumbnailTop, bottom: thumbnailBottom, height: thumbnailHeight } = currentThumbnailListItem.getBoundingClientRect();

        if (thumbnailTop < listTop) {
          const top = currentThumbnailListItem.offsetTop - 5;

          this.elements.thumbnailList.scrollTo({
            left: 0,
            top: top,
            behavior: Themu.prefersReducedMotion ? 'instant' : 'smooth',
          });
        }

        if (thumbnailBottom > listBottom) {
          const top = currentThumbnailListItem.offsetTop - listHeight + thumbnailHeight + 5;

          this.elements.thumbnailList.scrollTo({
            left: 0,
            top: top,
            behavior: Themu.prefersReducedMotion ? 'instant' : 'smooth',
          });
        }
      }
    }
  }

  customElements.define('ce-product-media', ProductMedia);
})();
